import { CommonModule } from '@angular/common';
import { Component, computed, ElementRef, inject, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { formatMultiplier, OptionType } from '../../enums/OptionType';
import { OnlineOrderType } from '../../models/OnlineOrder.model';
import { OrderItem } from '../../models/OrderItem.model';
import { AppSettingsService } from '../../services/app-settings.service';
import { CartService } from '../../services/cart.service';
import { GeoService } from '../../services/geo.service';
import { FormatPricePipe } from '../../utility/pipes/format-price.pipe';
import { TextUtils } from '../../utility/text.utils';

@Component({
  selector: 'app-cart-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, FormatPricePipe],
  templateUrl: './cart-modal.html',
})
export class CartModalComponent {
  public cartService = inject(CartService);
  public settingsService = inject(AppSettingsService);
  public geoService = inject(GeoService);
  private translate = inject(TranslateService);

  @ViewChild('scrollArea') scrollArea?: ElementRef<HTMLDivElement>;
  @ViewChild('tippingEnd') tippingEnd?: ElementRef<HTMLDivElement>;

  OnlineOrderType = OnlineOrderType;
  OptionType = OptionType;
  formatMultiplier = formatMultiplier;

  activeMenuLang = this.settingsService.activeMenuLanguage;
  currency = this.settingsService.selectedCurrency;
  tableID = this.settingsService.tableID;
  currentTable = this.settingsService.currentTable;

  get companyCurrency(): string {
    return this.currency()?.Symbol || '$';
  }

  get currencyPrecision(): number {
    const p = this.currency()?.Precision;
    return typeof p === 'number' && !isNaN(p) ? p : 2;
  }

  tableDisplayName = computed(() => {
    const table = this.currentTable();
    if (table?.Name) return table.Name;
    const tid = this.tableID();
    return tid ? `#${tid}` : '';
  });

  getDisplayText(value: any): string {
    return TextUtils.getLocalizedText(value, this.activeMenuLang()?.Code);
  }

  close() {
    this.cartService.closeCart();
  }

  setOrderType(type: OnlineOrderType) {
    if (type === OnlineOrderType.DINE_IN && (!this.tableID() || !this.settingsService.tableOrderEnabled())) {
      return;
    }
    if (type === OnlineOrderType.PICKUP && !this.settingsService.pickupOrderEnabled()) {
      return;
    }
    this.cartService.setOrderType(type);
  }

  setPaymentType(type: number) {
    if (this.cartService.isPaymentRequired() && type === 0) {
      return;
    }
    this.cartService.selectedPaymentType.set(type);
  }

  readonly tipPresets: (number | 'custom')[] = [0, 5, 10, 15, 'custom'];

  getPresetTipAmount(preset: number): number {
    if (preset <= 0) return 0;
    return Number(((this.cartService.grossTotal() * preset) / 100).toFixed(this.currencyPrecision));
  }

  setTipPreset(preset: number | 'custom') {
    this.cartService.setTipPreset(preset);
    this.scrollToTipping();
  }

  onCustomTipInput(value: string | number) {
    const num = typeof value === 'number' ? value : parseFloat(value) || 0;
    this.cartService.setCustomTip(num);
    this.scrollToTipping();
  }

  private scrollToTipping() {
    setTimeout(() => {
      if (this.tippingEnd?.nativeElement) {
        this.tippingEnd.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      } else if (this.scrollArea?.nativeElement) {
        this.scrollArea.nativeElement.scrollTo({
          top: this.scrollArea.nativeElement.scrollHeight,
          behavior: 'smooth',
        });
      }
    }, 60);
  }

  onEmailChange(value: string) {
    this.cartService.setCustomerEmail(value);
  }

  updateQuantity(item: OrderItem, delta: number) {
    if (item.id) {
      this.cartService.updateQuantity(item.id, delta);
    }
  }

  removeItem(item: OrderItem) {
    if (item.id) {
      this.cartService.removeItem(item.id);
    }
  }

  async onSendOrder() {
    if (!this.cartService.isRestaurantOpen()) {
      const status = this.cartService.operatingStatus();
      let msg = this.translate.instant('OPERATING_HOURS.CART_CLOSED_MSG') || 'The restaurant is currently closed.';
      if (status.opensAtTime) {
        const timeMsg = status.messageKey === 'OPERATING_HOURS.OPENS_TODAY_AT'
          ? (this.translate.instant('OPERATING_HOURS.OPENS_TODAY_AT', { time: status.opensAtTime }) || `Opens today at ${status.opensAtTime} UTC`)
          : status.messageKey === 'OPERATING_HOURS.OPENS_TOMORROW_AT'
            ? (this.translate.instant('OPERATING_HOURS.OPENS_TOMORROW_AT', { time: status.opensAtTime }) || `Opens tomorrow at ${status.opensAtTime} UTC`)
            : (this.translate.instant('OPERATING_HOURS.OPENS_DAY_AT', { day: status.nextOpenDay, time: status.opensAtTime }) || `Opens ${status.nextOpenDay} at ${status.opensAtTime} UTC`);
        msg += ` (${timeMsg})`;
      }
      this.cartService.submitError.set(msg);
      alert(msg);
      return;
    }

    if (this.cartService.isBelowMinOrder()) {
      const min = this.cartService.minOrderTotal();
      const rem = this.cartService.minOrderRemaining();
      const sym = this.companyCurrency;
      const prec = this.currencyPrecision;
      const formattedRem = typeof rem === 'number' ? rem.toFixed(prec) : rem;
      const formattedMin = typeof min === 'number' ? min.toFixed(prec) : min;
      const msg = this.translate.instant('LIMITS.MIN_ORDER_HELP', {
        remaining: `${sym} ${formattedRem}`,
        min: `${sym} ${formattedMin}`,
      }) || `Minimum order amount is ${sym} ${formattedMin}. Please add ${sym} ${formattedRem} more to submit your order.`;
      this.cartService.submitError.set(msg);
      alert(msg);
      return;
    }

    if (this.cartService.isAboveMaxOrder()) {
      const max = this.cartService.maxOrderTotal();
      const sym = this.companyCurrency;
      const prec = this.currencyPrecision;
      const formattedMax = typeof max === 'number' ? max.toFixed(prec) : max;
      const msg = this.translate.instant('LIMITS.MAX_ORDER_WARN', {
        max: `${sym} ${formattedMax}`,
      }) || `Order total exceeds the maximum limit of ${sym} ${formattedMax}.`;
      this.cartService.submitError.set(msg);
      alert(msg);
      return;
    }

    try {
      await this.cartService.submitOrder();
    } catch (e: any) {
      // Error is caught and surfaced in cartService.submitError
      if (e?.message) {
        alert(e.message);
      }
    }
  }
}

